// src/utils/markdownFormatter.ts (New Utility Module)
// All logic extracted directly from src/components/TagTriggers.tsx

export const TOKENS = [
  { pre: "**", post: "**" }, // bold
  { pre: "*", post: "*" },   // italic
  { pre: "==", post: "==" }, // highlight
] as const;
export type Token = (typeof TOKENS)[number];

// Feature flags for multiline behavior (peel-first + no Word-style flip)
const FF_PEEL_FIRST_MULTILINE = true;
const FF_DISABLE_WORDSTYLE_FLIP = true;

type ToggleResult = { next: string; selStart: number; selEnd: number };

function sliceEq(s: string, i: number, token: string) {
  return i >= 0 && i + token.length <= s.length && s.slice(i, i + token.length) === token;
}

/** We never want * to “grab” part of ** or ***. */
function isStandaloneAt(s: string, i: number, token: string) {
  const same = token[token.length - 1];
  const leftSame = i - 1 >= 0 && s[i - 1] === same;
  const rightSame = i + token.length < s.length && s[i + token.length] === same;
  return !leftSame && !rightSame;
}

/** 🔍 NEW: detect if a line body contains ANY standalone instance of the given token. */
function lineHasAnyStandalone(body: string, tok: Token): boolean {
  // Generic: find a balanced standalone open/close pair of `token` anywhere in the string.
  const hasBalancedStandalonePair = (s: string, token: string) => {
    let open = s.indexOf(token, 0);
    while (open !== -1) {
      if (isStandaloneAt(s, open, token)) {
        let close = s.indexOf(token, open + token.length);
        while (close !== -1) {
          if (isStandaloneAt(s, close, token)) return true;
          close = s.indexOf(token, close + 1);
        }
      }
      open = s.indexOf(token, open + 1);
    }
    return false;
  };

  // == highlight
  if (tok.pre === "==") {
    return hasBalancedStandalonePair(body, "==");
  }

  // bold
  if (tok.pre === "**") {
    // Accept either **…** or ***…*** as "has a bold-ish layer" (triple implies bold)
    if (hasBalancedStandalonePair(body, "**")) return true;
    if (hasBalancedStandalonePair(body, "***")) return true;
    return false;
  }

  // italic
  if (tok.pre === "*") {
    // Accept *…* or ***…*** (triple implies italic)
    if (hasBalancedStandalonePair(body, "***")) return true;
    // For single *, ensure we don't accidentally count ** runs (standalone logic excludes them)
    return hasBalancedStandalonePair(body, "*");
  }

  return false;
}

/** Push selection past a leading "- " on its current line to avoid formatting the bullet. */
function skipLeadingDashOnLine(value: string, start: number, end: number) {
  const lineStart = value.lastIndexOf("\n", start - 1) + 1;
  const rest = value.slice(lineStart);
  const m = rest.match(/^\s*-\s?/);
  if (m) {
    const bulletEnd = lineStart + m[0].length;
    if (start <= bulletEnd) {
      start = bulletEnd;
      end = Math.max(end, start);
    }
  }
  return { start, end };
}

/**
 * Handle ***triple*** markers first for bold/italic only.
 * If selection is wrapped by ***…*** and user toggles:
 * - Italic (*)  => degrade to **…** (remove italic)
 * - Bold (**)   => degrade to *…* (remove bold)
 */
function tryUnwrapTriple(value: string, start: number, end: number, tok: Token): ToggleResult | null {
  const left3_outside = start >= 3 && value.slice(start - 3, start) === "***";
  const right3_outside = value.slice(end, end + 3) === "***";
  const left3_atEdge = value.slice(start, start + 3) === "***";
  const right3_atEdge = end >= 3 && value.slice(end - 3, end) === "***";

  // Case A: markers immediately outside selection: ***[sel]***
  if (left3_outside && right3_outside) {
    const before = value.slice(0, start - 3);
    const inner = value.slice(start, end);
    const after = value.slice(end + 3);
    const replacement = tok.pre === "*" ? "**" : "*";
    const next = before + replacement + inner + replacement + after;
    const delta = 3 - replacement.length;
    const selStart = start - delta;
    const selEnd = end - delta;
    return { next, selStart, selEnd };
  }

  // Case B: markers exactly at selection edges: [***sel***]
  if (left3_atEdge && right3_atEdge) {
    const before = value.slice(0, start);
    const inner = value.slice(start + 3, end - 3);
    const after = value.slice(end);
    const replacement = tok.pre === "*" ? "**" : "*";
    const next = before + replacement + inner + replacement + after;
    const selStart = start;
    const selEnd = start + replacement.length + inner.length + replacement.length;
    return { next, selStart, selEnd };
  }

  return null;
}

/** Helpers used by multi-line toggling */
function splitBulletAware(ln: string) {
  const m = ln.match(/^\s*-\s?/);
  const prefixLen = m ? m[0].length : 0;
  return { head: ln.slice(0, prefixLen), body: ln.slice(prefixLen) };
}

/** Envelope-aware: detects presence of tok in common outer/inner combos (incl. highlight). */
function lineHasTokenBody(body: string, tok: Token) {
  // Helper: check a token exactly at both edges AND standalone (not part of a longer run like ** or ***)
  const hasStandaloneAtEdges = (b: string, t: Token) => {
    if (!b.startsWith(t.pre) || !b.endsWith(t.post)) return false;
    const startOk = isStandaloneAt(b, 0, t.pre);
    const endOk = isStandaloneAt(b, b.length - t.post.length, t.post);
    return startOk && endOk;
  };

  // Plain triple implies BOTH bold and italic
  const isPlainTriple = body.startsWith("***") && body.endsWith("***");

  // Highlight: must be ==…== with standalone ==
  if (tok.pre === "==") {
    return hasStandaloneAtEdges(body, tok);
  }

  // BOLD
  if (tok.pre === "**") {
    if (hasStandaloneAtEdges(body, tok)) return true;
    if (isPlainTriple) return true;
    if (body.startsWith("==") && body.endsWith("==")) {
      const inner = body.slice(2, -2);
      if (inner.startsWith("***") && inner.endsWith("***")) return true;
      if (
        inner.startsWith("**") &&
        inner.endsWith("**") &&
        isStandaloneAt(inner, 0, "**") &&
        isStandaloneAt(inner, inner.length - 2, "**")
      ) {
        return true;
      }
    }
    return false;
  }

  // ITALIC
  if (tok.pre === "*") {
    if (body.startsWith("**") && body.endsWith("**")) return false;
    if (hasStandaloneAtEdges(body, tok)) return true;
    if (isPlainTriple) return true;
    if (body.startsWith("==") && body.endsWith("==")) {
      const inner = body.slice(2, -2);
      if (inner.startsWith("***") && inner.endsWith("***")) return true;
      if (
        inner.startsWith("*") &&
        inner.endsWith("*") &&
        !inner.startsWith("**") &&
        !inner.endsWith("**") &&
        isStandaloneAt(inner, 0, "*") &&
        isStandaloneAt(inner, inner.length - 1, "*")
      ) {
        return true;
      }
    }
    return false;
  }

  return false;
}

function tryUnwrapOne(value: string, start: number, end: number, tok: Token): ToggleResult | null {
  // Case 1: markers immediately outside the selection
  if (
    start >= tok.pre.length &&
    sliceEq(value, start - tok.pre.length, tok.pre) &&
    isStandaloneAt(value, start - tok.pre.length, tok.pre) &&
    sliceEq(value, end, tok.post) &&
    isStandaloneAt(value, end, tok.post)
  ) {
    const before = value.slice(0, start - tok.pre.length);
    const inner = value.slice(start, end);
    const after = value.slice(end + tok.post.length);
    const next = before + inner + after;
    const cursorStart = start - tok.pre.length;
    const cursorEnd = end - tok.pre.length;
    return { next, selStart: cursorStart, selEnd: cursorEnd };
  }

  // Case 2: markers lie exactly at selection edges
  if (
    sliceEq(value, start, tok.pre) &&
    isStandaloneAt(value, start, tok.pre) &&
    end - tok.post.length >= 0 &&
    sliceEq(value, end - tok.post.length, tok.post) &&
    isStandaloneAt(value, end - tok.post.length, tok.post)
  ) {
    const before = value.slice(0, start);
    const inner = value.slice(start + tok.pre.length, end - tok.post.length);
    const after = value.slice(end);
    const next = before + inner + after;
    const cursorStart = start;
    const cursorEnd = start + inner.length;
    return { next, selStart: cursorStart, selEnd: cursorEnd };
  }

  // Case 3: find a balanced pair fully inside selection
  let open = value.indexOf(tok.pre, start);
  while (open !== -1 && open < end) {
    if (!isStandaloneAt(value, open, tok.pre)) {
      open = value.indexOf(tok.pre, open + 1);
      continue;
    }
    const close = value.indexOf(tok.post, open + tok.pre.length);
    if (close !== -1 && close < end && isStandaloneAt(value, close, tok.post)) {
      const before = value.slice(0, open);
      const inner = value.slice(open + tok.pre.length, close);
      const after = value.slice(close + tok.post.length);
      const next = before + inner + after;

      const removedBeforeSelection =
        (open < start ? tok.pre.length : 0) + (close < end ? tok.post.length : 0);
      return {
        next,
        selStart: start - (open < start ? tok.pre.length : 0),
        selEnd: end - removedBeforeSelection,
      };
    }
    open = value.indexOf(tok.pre, open + 1);
  }

  return null;
}

/** New: bullet-aware multi-line toggle
 * Rule: if every non-empty line already has tok → unwrap (peel/degrade) appropriately; else → wrap only missing.
 * Keeps the selection exactly over the transformed block.
 */
function toggleMultiline(value: string, start: number, end: number, tok: Token): ToggleResult {
  const before = value.slice(0, start);
  const block = value.slice(start, end);
  const after = value.slice(end);

  const lines = block.split("\n");

  type LineInfo = { ln: string; empty: boolean; head: string; body: string; has: boolean };

  const info: LineInfo[] = lines.map((ln) => {
    if (ln.trim() === "") return { ln, empty: true, head: "", body: "", has: false };
    const { head, body } = splitBulletAware(ln);
    const has = lineHasTokenBody(body, tok);
    return { ln, empty: false, head, body, has };
  });

  const allHave = info.every((it) => it.empty || it.has);

  // If selection is multi-line and contains ANY instance of tok on ANY line, do a PEEL pass first.
  const peelFirst =
    FF_PEEL_FIRST_MULTILINE &&
    FF_DISABLE_WORDSTYLE_FLIP &&
    info.some((it) => !it.empty && lineHasAnyStandalone(it.body, tok));

  if (!allHave && peelFirst) {
    const peeledLines = info.map((it) => {
      if (it.empty) return it.ln;
      const { head, body } = it;

      if (tok.pre === "==") {
        const un = tryUnwrapOne(body, 0, body.length, tok);
        return head + (un ? un.next : body);
      }

      // Bold/Italic peel: prefer triple-degrade, then pair; operate inside ==…== if present.
      if (body.startsWith("==") && body.endsWith("==")) {
        const inner = body.slice(2, -2);
        const tri = tryUnwrapTriple(inner, 0, inner.length, tok);
        if (tri) return head + "==" + tri.next + "==";
        const un = tryUnwrapOne(inner, 0, inner.length, tok);
        if (un) return head + "==" + un.next + "==";
        return head + body;
      }

      const tri = tryUnwrapTriple(body, 0, body.length, tok);
      if (tri) return head + tri.next;
      const un = tryUnwrapOne(body, 0, body.length, tok);
      if (un) return head + un.next;

      return head + body;
    });

    const transformed = peeledLines.join("\n");
    const next = before + transformed + after;
    return { next, selStart: start, selEnd: start + transformed.length };
  }

  const transformedLines = info.map((it) => {
    if (it.empty) return it.ln;

    const { head, body } = it;

    // ---------- UNWRAP/PEEL PATH ----------
    if (allHave) {
      if (tok.pre === "==") {
        if (body.startsWith("==") && body.endsWith("==")) {
          return head + body.slice(2, -2);
        }
        return head + body;
      }

      if (body.startsWith("***") && body.endsWith("***")) {
        const tri = tryUnwrapTriple(body, 0, body.length, tok);
        return head + (tri ? tri.next : body);
      }

      if (body.startsWith("==") && body.endsWith("==")) {
        const inner = body.slice(2, -2);
        if (inner.startsWith("***") && inner.endsWith("***")) {
          const tri = tryUnwrapTriple(inner, 0, inner.length, tok);
          return head + "==" + (tri ? tri.next : inner) + "==";
        }
        if (inner.startsWith(tok.pre) && inner.endsWith(tok.post)) {
          const un = tryUnwrapOne(inner, 0, inner.length, tok);
          return head + "==" + (un ? un.next : inner) + "==";
        }
        return head + body;
      }

      if (body.startsWith(tok.pre) && body.endsWith(tok.post)) {
        const un = tryUnwrapOne(body, 0, body.length, tok);
        return head + (un ? un.next : body);
      }

      return head + body;
    }

    // ---------- WRAP-MISSING PATH ----------
    if (it.has) return it.ln;

    if (tok.pre === "*") {
      const hasItalicAlready =
        ((body.startsWith("*") && body.endsWith("*") && !(body.startsWith("**") && body.endsWith("**"))) ||
          (body.startsWith("***") && body.endsWith("***")) ||
          (body.startsWith("==") &&
            body.endsWith("==") &&
            (() => {
              const inner = body.slice(2, -2);
              return (
                (inner.startsWith("*") &&
                  inner.endsWith("*") &&
                  !(inner.startsWith("**") && inner.endsWith("**"))) ||
                (inner.startsWith("***") && inner.endsWith("***"))
              );
            })()));

      if (!hasItalicAlready) {
        if (
          body.startsWith("**") &&
          body.endsWith("**") &&
          isStandaloneAt(body, 0, "**") &&
          isStandaloneAt(body, body.length - 2, "**")
        ) {
          const core = body.slice(2, -2);
          return head + "***" + core + "***";
        }

        if (body.startsWith("==") && body.endsWith("==")) {
          const inner = body.slice(2, -2);
          if (
            inner.startsWith("**") &&
            inner.endsWith("**") &&
            isStandaloneAt(inner, 0, "**") &&
            isStandaloneAt(inner, inner.length - 2, "**")
          ) {
            const core = inner.slice(2, -2);
            return head + "==" + "***" + core + "***" + "==";
          }
        }
      }
    }

    if (tok.pre === "==") {
      return head + "==" + body + "==";
    }

    if (body.startsWith("==") && body.endsWith("==")) {
      const inner = body.slice(2, -2);
      const tri = tryUnwrapTriple(inner, 0, inner.length, tok);
      const un = tri ?? tryUnwrapOne(inner, 0, inner.length, tok);
      if (un) return head + "==" + un.next + "==";
      const wrapped = wrapSelection(inner, 0, inner.length, tok);
      return head + "==" + wrapped.next + "==";
    }

    return head + tok.pre + body + tok.post;
  });

  // 🔧 Normalize accidental ****…**** → **…** (bullet-aware + plain)
  const normalizedLines = transformedLines.map((ln) => {
    const m = ln.match(/^(\s*-\s?)(\*{4})(.*?)(\*{4})$/);
    if (m) return `${m[1]}**${m[3]}**`;
    if (/^\*{4}.*\*{4}$/.test(ln)) {
      return ln.replace(/^\*{4}(.*)\*{4}$/, "**$1**");
    }
    return ln;
  });

  const transformed = normalizedLines.join("\n");
  const next = before + transformed + after;
  return { next, selStart: start, selEnd: start + transformed.length };
}

function wrapSelection(value: string, start: number, end: number, tok: Token): ToggleResult {
  const selected = value.slice(start, end);
  const before = value.slice(0, start);
  const after = value.slice(end);
  const innerLen = end - start;
  const next = before + tok.pre + selected + tok.post + after;
  const selStart = start;
  const selEnd = start + tok.pre.length + innerLen + tok.post.length;
  return { next, selStart, selEnd };
}

/** Detect selection within == fences (outside or exactly at edges) */
function selectionWithinHighlight(value: string, start: number, end: number) {
  const leftHL_outside = start >= 2 && value.slice(start - 2, start) === "==";
  const rightHL_outside = value.slice(end, end + 2) === "==";
  const leftHL_atEdge = value.slice(start, start + 2) === "==";
  const rightHL_atEdge = end >= 2 && value.slice(end - 2, end) === "==";

  if (leftHL_outside && rightHL_outside) {
    return { type: "outside" as const, innerStart: start, innerEnd: end, fenceStart: start - 2, fenceEnd: end + 2 };
  }
  if (leftHL_atEdge && rightHL_atEdge) {
    return { type: "edge" as const, innerStart: start + 2, innerEnd: end - 2, fenceStart: start, fenceEnd: end };
  }
  return null;
}

/** Detect selection within *** fences (outside or exactly at edges) */
function selectionWithinTriple(value: string, start: number, end: number) {
  const left3_outside = start >= 3 && value.slice(start - 3, start) === "***";
  const right3_outside = value.slice(end, end + 3) === "***";
  const left3_atEdge = value.slice(start, start + 3) === "***";
  const right3_atEdge = end >= 3 && value.slice(end - 3, end) === "***";

  if (left3_outside && right3_outside) {
    return { type: "outside" as const, innerStart: start, innerEnd: end, fenceStart: start - 3, fenceEnd: end + 3 };
  }
  if (left3_atEdge && right3_atEdge) {
    return { type: "edge" as const, innerStart: start + 3, innerEnd: end - 3, fenceStart: start, fenceEnd: end };
  }
  return null;
}

/** Helper: perform unwrap/wrap on a small segment WITHOUT considering == fences (avoids recursion) */
function toggleOnSegmentNoHighlight(seg: string, tok: Token): ToggleResult {
  const start = 0;
  const end = seg.length;

  if (tok.pre !== "==") {
    const tri = tryUnwrapTriple(seg, start, end, tok);
    if (tri) return tri;
  }

  const un = tryUnwrapOne(seg, start, end, tok);
  if (un) return un;

  return wrapSelection(seg, start, end, tok);
}

/** Interior fail-safe: strip ONE inner instance of the same style within [start,end] */
function stripOneInnerInstance(value: string, start: number, end: number, tok: Token): { next: string; selStart: number; selEnd: number } {
  let open = value.indexOf(tok.pre, start);
  while (open !== -1 && open < end) {
    if (!isStandaloneAt(value, open, tok.pre)) {
      open = value.indexOf(tok.pre, open + 1);
      continue;
    }
    const close = value.indexOf(tok.post, open + tok.pre.length);
    if (close !== -1 && close < end && isStandaloneAt(value, close, tok.post)) {
      const before = value.slice(0, open);
      const inner = value.slice(open + tok.pre.length, close);
      const after = value.slice(close + tok.post.length);
      const next = before + inner + after;
      const removedBeforeSelection =
        (open < start ? tok.pre.length : 0) + (close < end ? tok.post.length : 0);
      return {
        next,
        selStart: start - (open < start ? tok.pre.length : 0),
        selEnd: end - removedBeforeSelection,
      };
    }
    open = value.indexOf(tok.pre, open + 1);
  }
  return { next: value, selStart: start, selEnd: end };
}

/** ------------------------------ Main toggle ------------------------------ */
export function toggleOrWrap(value: string, start: number, end: number, tok: Token): ToggleResult {
  const isMultiline = value.slice(start, end).includes("\n");

  if (!isMultiline) {
    ({ start, end } = skipLeadingDashOnLine(value, start, end));
    if (start === end) return { next: value, selStart: start, selEnd: end };
  } else {
    if (start === end) return { next: value, selStart: start, selEnd: end };
  }

  if (start < end && value.slice(start, end).trim() === "") {
    return { next: value, selStart: start, selEnd: end };
  }

  const hl = selectionWithinHighlight(value, start, end);
  const triEnv = selectionWithinTriple(value, start, end);
  const atCap = !!hl && !!triEnv;

  /** CAP BEHAVIOR */
  if (atCap && !isMultiline) {
    if (tok.pre === "==") {
      if (hl) {
        const before = value.slice(0, hl.fenceStart);
        const inner = value.slice(hl.innerStart, hl.innerEnd);
        const after = value.slice(hl.fenceEnd);
        const next = before + inner + after;
        const selStart = before.length;
        const selEnd = selStart + inner.length;
        return { next, selStart, selEnd };
      }
    } else {
      if (hl) {
        const before = value.slice(0, hl.fenceStart);
        const inner = value.slice(hl.innerStart, hl.innerEnd);
        const after = value.slice(hl.fenceEnd);
        const r: ToggleResult = tryUnwrapTriple(inner, 0, inner.length, tok) ?? toggleOnSegmentNoHighlight(inner, tok);
        const next = before + "==" + r.next + "==" + after;
        const selStart = before.length;
        const selEnd = selStart + 2 + r.next.length + 2;
        return { next, selStart, selEnd };
      }
      const tri = tryUnwrapTriple(value, start, end, tok);
      if (tri) return tri;
    }
  }

  // Inside ==…== on single line: operate within fences
  if (tok.pre !== "==") {
    const hl2 = !isMultiline ? selectionWithinHighlight(value, start, end) : null;
    if (hl2) {
      const before = value.slice(0, hl2.fenceStart);
      const inner = value.slice(hl2.innerStart, hl2.innerEnd);
      const after = value.slice(hl2.fenceEnd);

      const tri = tryUnwrapTriple(inner, 0, inner.length, tok);
      const un = tri ?? tryUnwrapOne(inner, 0, inner.length, tok);
      let seg: ToggleResult;
      if (un) {
        seg = un;
      } else {
        const stripped = stripOneInnerInstance(inner, 0, inner.length, tok);
        const wrapped = wrapSelection(stripped.next, stripped.selStart, stripped.selEnd, tok);
        seg = wrapped;
      }

      const next = before + "==" + seg.next + "==" + after;
      const selStart = before.length;
      const selEnd = selStart + 2 + seg.next.length + 2;
      return { next, selStart, selEnd };
    }
  }

  if (tok.pre !== "==") {
    if (!isMultiline) {
      const tri = tryUnwrapTriple(value, start, end, tok);
      if (tri) return tri;
    }
  }

  if (isMultiline) {
    return toggleMultiline(value, start, end, tok);
  }

  const un = tryUnwrapOne(value, start, end, tok);
  if (un) return un;

  const stripped = stripOneInnerInstance(value, start, end, tok);
  const wrapped = wrapSelection(stripped.next, stripped.selStart, stripped.selEnd, tok);
  return wrapped;
}